import React, { useEffect, useState } from "react";
import { HandHeart, Loader2, Pencil, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function PrayerRequests() {
  const [text, setText] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/prayers").then((r) => { setList(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/prayers", { text, anonymous, is_public: isPublic });
      toast.success("Your prayer request has been received 🙏");
      setText(""); setAnonymous(false); setIsPublic(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); } finally { setSubmitting(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this prayer request?")) return;
    try { await api.delete(`/prayers/${id}`); toast.success("Prayer request deleted"); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  const saveEdit = async () => {
    if (!editing.text.trim()) { toast.error("Please enter your prayer request"); return; }
    try {
      await api.put(`/prayers/${editing.id}`, { text: editing.text, anonymous: editing.anonymous, is_public: editing.is_public });
      toast.success("Prayer request updated");
      setEditing(null); load();
    } catch (e) { toast.error("Could not update"); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-10 max-w-3xl">
      <div className="flex items-center gap-3 mb-1">
        <HandHeart className="w-6 h-6 text-[#E6B34A]" />
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">Prayer Requests</h1>
      </div>
      <p className="text-zinc-400 mb-8">Share what's on your heart. Our team will pray over every request.</p>

      <form onSubmit={submit} className="rounded-2xl border border-white/5 bg-[#141C42] p-6 mb-8" data-testid="prayer-form">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Type your prayer request…" data-testid="prayer-text" className="bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] min-h-[120px]" />
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-4">
          <div className="flex flex-col gap-2.5">
            <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} data-testid="prayer-anon" className="accent-[#E6B34A]" /> Submit anonymously
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} data-testid="prayer-public" className="accent-[#E6B34A]" /> Share with the community on the Prayer Wall
            </label>
          </div>
          <button type="submit" disabled={submitting} data-testid="prayer-submit" className="px-6 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60 shrink-0">
            {submitting ? "Sending…" : "Send Request"}
          </button>
        </div>
      </form>

      <h2 className="font-display font-extrabold text-lg tracking-tight mb-4">Your requests</h2>
      {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#E6B34A]" /></div> :
        list.length === 0 ? <p className="text-zinc-500">You haven't submitted any prayer requests yet.</p> :
        <div className="space-y-3" data-testid="prayer-list">
          {list.map((p) => (
            <div key={p.id} className="rounded-2xl border border-white/5 bg-[#141C42] p-5 flex items-start justify-between gap-3" data-testid={`prayer-${p.id}`}>
              <p className="text-sm text-zinc-300 min-w-0 flex-1">{p.text}</p>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <span className={`overline px-2.5 py-1 rounded-full ${p.status === "prayed" ? "text-emerald-400 bg-emerald-400/10" : "text-[#E6B34A] bg-[#E6B34A]/10"}`}>{p.status === "prayed" ? "Prayed 🙏" : "Open"}</span>
                <div className="flex gap-1.5">
                  <button onClick={() => setEditing({ id: p.id, text: p.text, anonymous: !!p.anonymous, is_public: !!p.is_public })} data-testid={`prayer-edit-${p.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                  <button onClick={() => del(p.id)} data-testid={`prayer-del-${p.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-white/5 text-zinc-400 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#141C42] border-white/10 text-white" data-testid="prayer-edit-dialog">
          <DialogHeader><DialogTitle className="font-display font-extrabold text-2xl tracking-tight">Edit Prayer Request</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 mt-2">
              <Textarea value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} data-testid="edit-prayer-text" className="bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] min-h-[120px]" />
              <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
                <input type="checkbox" checked={editing.anonymous} onChange={(e) => setEditing({ ...editing, anonymous: e.target.checked })} data-testid="edit-prayer-anon" className="accent-[#E6B34A]" /> Submit anonymously
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
                <input type="checkbox" checked={editing.is_public} onChange={(e) => setEditing({ ...editing, is_public: e.target.checked })} data-testid="edit-prayer-public" className="accent-[#E6B34A]" /> Share on the Prayer Wall
              </label>
              <button onClick={saveEdit} data-testid="save-prayer-edit" className="w-full py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors">Save changes</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
